import { Component, OnDestroy, signal } from "@angular/core";
import { RouterOutlet, NavigationEnd, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { filter } from "rxjs";

import { PrivateHeaderComponent } from "./private-header/private-header.component";
import { PrivateSidebarComponent } from "./private-sidebar/private-sidebar.component";
import type { HeaderMode } from "./private-header/private-header.component";
import { itemsPrivatePage } from "@models/items-private-page";

export type HeaderConfig = {
  mode: HeaderMode;
  title?: string;
  subtitle?: string;
  titleKey?: string;
  subtitleKey?: string;
  titleParams?: Record<string, any>;
  subtitleParams?: Record<string, any>;
  showBack?: boolean;
  backTo?: string | (() => void);
  icon?: string;
};

@Component({
  selector: "app-layout-private",
  standalone: true,
  imports: [
    RouterOutlet,
    PrivateHeaderComponent,
    PrivateSidebarComponent,
    CommonModule,
  ],
  templateUrl: "./layout-private.component.html",
  styleUrl: "./layout-private.component.scss",
})
export class LayoutPrivateComponent implements OnDestroy {
  readonly isMobile = signal(false);
  readonly menuOpen = signal(true);

  readonly headerMode = signal<HeaderMode>("default");
  readonly headerTitle = signal<string>("");
  readonly headerSubtitle = signal<string>("");
  readonly headerIcon = signal<string>("");
  readonly headerShowBack = signal<boolean>(false);
  headerBackTo?: string | (() => void);

  readonly headerTitleKey = signal<string | undefined>(undefined);
  readonly headerSubtitleKey = signal<string | undefined>(undefined);

  readonly headerTitleParams = signal<Record<string, any> | undefined>(undefined);
  readonly headerSubtitleParams = signal<Record<string, any> | undefined>(undefined);

  private readonly mq = window.matchMedia("(max-width: 768px)");
  private readonly onMqChange = (e: MediaQueryListEvent) => this.applyViewport(e.matches);

  private readonly onVehicleHeader = (ev: Event) => {
    const anyEv = ev as CustomEvent<HeaderConfig>;
    const cfg = anyEv.detail;
    if (!cfg) return;

    this.headerMode.set(cfg.mode as HeaderMode);

    this.headerTitle.set(cfg.title ?? "");
    this.headerSubtitle.set(cfg.subtitle ?? "");
    this.headerTitleKey.set(cfg.titleKey);
    this.headerSubtitleKey.set(cfg.subtitleKey);
    this.headerTitleParams.set(cfg.titleParams);
    this.headerSubtitleParams.set(cfg.subtitleParams);

    this.headerShowBack.set(!!cfg.showBack);
    this.headerBackTo = cfg.backTo;
    this.headerIcon.set(cfg.icon ?? "");
  };

  constructor(private readonly router: Router) {
    this.applyViewport(this.mq.matches);
    this.mq.addEventListener("change", this.onMqChange);

    this.setupTitleSync();
    window.addEventListener("vehicle-header", this.onVehicleHeader);
  }

  ngOnDestroy() {
    this.mq.removeEventListener("change", this.onMqChange);
    window.removeEventListener("vehicle-header", this.onVehicleHeader);
  }

  private applyViewport(isMob: boolean) {
    this.isMobile.set(isMob);
    this.menuOpen.set(isMob ? false : true);
  }

  toggleMenu() {
    this.menuOpen.update((v) => !v);
  }

  closeMenuIfMobile() {
    if (this.isMobile()) this.menuOpen.set(false);
  }

  private setupTitleSync() {
    this.applyDefaultTitleFromUrl();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.applyDefaultTitleFromUrl());
  }

  onActivate(comp: any) {
    const cfg =
      comp?.headerConfig ??
      (typeof comp?.getHeaderConfig === "function" ? comp.getHeaderConfig() : undefined);

    if (cfg?.mode === "page") {
      this.headerMode.set("page");

      this.headerTitle.set(cfg.title ?? "");
      this.headerSubtitle.set(cfg.subtitle ?? "");
      this.headerTitleKey.set(cfg.titleKey);
      this.headerSubtitleKey.set(cfg.subtitleKey);
      this.headerTitleParams.set(cfg.titleParams);
      this.headerSubtitleParams.set(cfg.subtitleParams);

      this.headerShowBack.set(!!cfg.showBack);
      this.headerBackTo = cfg.backTo;
      this.headerIcon.set(cfg.icon ?? this.iconFromUrl(this.router.url));
    } else {
      this.headerMode.set("default");
      this.headerShowBack.set(false);
      this.headerBackTo = undefined;

      const url = this.router.url;

      this.headerTitleKey.set(this.titleKeyFromUrl(url));
      this.headerTitle.set(this.titleFromUrl(url));

      this.headerSubtitleKey.set(undefined);
      this.headerSubtitle.set("");

     
      this.headerTitleParams.set(undefined);
      this.headerSubtitleParams.set(undefined);

      this.headerIcon.set(this.iconFromUrl(url));
    }
  }

  onDeactivate() {
    this.headerMode.set("default");
    this.headerShowBack.set(false);
    this.headerBackTo = undefined;

    this.headerSubtitle.set("");
    this.headerSubtitleKey.set(undefined);
    this.headerTitleParams.set(undefined);
    this.headerSubtitleParams.set(undefined);

    this.headerIcon.set(this.iconFromUrl(this.router.url));
  }

  private applyDefaultTitleFromUrl() {
    if (this.headerMode() !== "default") return;
    const url = this.router.url;

    this.headerTitleKey.set(this.titleKeyFromUrl(url));
    this.headerTitle.set(this.titleFromUrl(url));
    this.headerTitleParams.set(undefined);
    this.headerSubtitleParams.set(undefined);
    this.headerIcon.set(this.iconFromUrl(url));
  }

  private flattenItems(items: typeof itemsPrivatePage): typeof itemsPrivatePage {
    const out: any[] = [];
    const walk = (arr: any[]) => {
      for (const it of arr) {
        out.push(it);
        if (Array.isArray(it.children) && it.children.length) walk(it.children);
      }
    };
    walk(items);
    return out as any;
  }

  private findItemByUrl(url: string) {
    const path = url.split("?")[0];
    const flat = this.flattenItems(itemsPrivatePage);
    return flat.find((i: any) => typeof i?.route === "string" && path.startsWith(i.route));
  }

  private iconFromUrl(url: string): string {
    const path = url.split("?")[0];

    if (path.includes("/financial")) return "fa-regular fa-money-bill-transfer";
    if (path.includes("/signature/plans/payments")) return "fa-solid fa-money-bill-wave";
    if (path.includes("/signature")) return "fa-solid fa-user";

    const item = this.findItemByUrl(url);
    return item?.icon ?? "";
  }

  private titleKeyFromUrl(url: string): string | undefined {
    const path = url.split("?")[0];

    if (path.includes("/financial")) return "SIDEBAR.FINANCIAL";
    if (path.includes("/signature/plans/payments")) return "SIDEBAR.PAYMENT";
    if (path.includes("/signature")) return "SIDEBAR.SUBSCRIPTION";

    const item = this.findItemByUrl(url) as any;
    return item?.titleKey;
  }

  private titleFromUrl(url: string): string {
    const path = url.split("?")[0];

    if (path.includes("/financial")) return "Financeiro";
    if (path.includes("/signature/plans/payments")) return "Pagamento";
    if (path.includes("/signature")) return "Assinatura";

    const item = this.findItemByUrl(url) as any;
    return item?.title ?? "";
  }
}
